import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import type {
  ClientCreateRequest,
  ClientDetailResponse,
  ClientDto,
  ClientListItem,
  ClientObjectDto,
  ClientObjectRequest,
  ClientObjectUpdateRequest,
  ClientUpdateRequest,
} from '@kominiarz/shared';
import { useEffect, useState } from 'react';
import { api } from './api';
import { queryClient } from './queryClient';
import { VISITS_KEY } from './visitsApi';

export const CLIENTS_KEY = ['clients'] as const;

/** Value that settles `delay` ms after the last change (search-as-you-type). */
export function useDebounced<T>(value: T, delay = 250): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setSettled(value), delay);
    return () => window.clearTimeout(t);
  }, [value, delay]);
  return settled;
}

export function useClientSearch(q: string, { enabled = true, limit = 30 }: { enabled?: boolean; limit?: number } = {}) {
  return useQuery({
    queryKey: [...CLIENTS_KEY, 'search', q, limit],
    queryFn: ({ signal }) =>
      api<{ clients: ClientListItem[]; hasMore: boolean }>(`/clients?q=${encodeURIComponent(q)}&limit=${limit}`, { signal }),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

export function useClient(id: number | null) {
  return useQuery({
    queryKey: [...CLIENTS_KEY, 'detail', id],
    queryFn: ({ signal }) => api<ClientDetailResponse>(`/clients/${id}`, { signal }),
    enabled: id !== null,
  });
}

const invalidateClients = () => {
  void queryClient.invalidateQueries({ queryKey: CLIENTS_KEY });
  // Visits show client names, phones and addresses.
  void queryClient.invalidateQueries({ queryKey: VISITS_KEY });
};

export function useCreateClient() {
  return useMutation({
    mutationFn: (body: ClientCreateRequest) => api<{ client: ClientDto }>('/clients', { method: 'POST', body }),
    onSuccess: invalidateClients,
  });
}

export function useUpdateClient(id: number) {
  return useMutation({
    mutationFn: (body: ClientUpdateRequest) => api<{ client: ClientDto }>(`/clients/${id}`, { method: 'PATCH', body }),
    onSuccess: invalidateClients,
  });
}

export function useAddObject(clientId: number) {
  return useMutation({
    mutationFn: (body: ClientObjectRequest) =>
      api<{ object: ClientObjectDto }>(`/clients/${clientId}/objects`, { method: 'POST', body }),
    onSuccess: invalidateClients,
  });
}

export function useUpdateObject() {
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: ClientObjectUpdateRequest }) =>
      api<{ object: ClientObjectDto }>(`/client-objects/${id}`, { method: 'PATCH', body }),
    onSuccess: invalidateClients,
  });
}

export function useRemoveObject() {
  return useMutation({
    mutationFn: (id: number) => api<{ ok: true }>(`/client-objects/${id}`, { method: 'DELETE' }),
    onSuccess: invalidateClients,
  });
}

export function useAnonymizeClient(id: number) {
  return useMutation({
    mutationFn: () => api<{ client: ClientDto; cancelledVisits: number }>(`/clients/${id}/anonymize`, { method: 'POST' }),
    onSuccess: invalidateClients,
  });
}
